import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { feeAPI, enrollmentAPI } from '../../services/api';
import { formatDateToIST, formatIndianCurrency } from '../../utils/dateUtils';
import InvoiceModal from '../InvoiceModal';

const PaymentForm = () => {
  const navigate = useNavigate();
  const { feeStructureId } = useParams();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  
  const installmentId = searchParams.get('installment');

  const [formData, setFormData] = useState({
    amount: '',
    payment_method: 'cash',
    transaction_id: '',
    cheque_number: '',
    bank_name: '',
    remarks: '',
    received_by: ''
  });

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  const { data: feeStructure, isLoading } = useQuery({
    queryKey: ['feeStructure', feeStructureId],
    queryFn: () => feeAPI.getById(feeStructureId).then(res => res.data),
    enabled: !!feeStructureId,
  });

  const { data: enrollments } = useQuery({
    queryKey: ['enrollments'],
    queryFn: () => enrollmentAPI.getAll(0, 100, true).then(res => res.data),
  });

  // Get the specific installment if installmentId is provided
  const selectedInstallment = installmentId && feeStructure?.installments?.find(
    inst => inst._id === installmentId || inst.id === installmentId
  );

  const createPaymentMutation = useMutation({
    mutationFn: (data) => feeAPI.createPayment(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['feeStructure', feeStructureId]);
      queryClient.invalidateQueries(['feeStructures']);
      
      // Show success message with option to generate invoice
      const paymentId = response.data?.payment?._id || response.data?.payment?.id;
      if (paymentId) {
        setPaymentData(response.data.payment);
        setShowInvoiceModal(true);
      } else {
        navigate(`/fees/${feeStructureId}/payments`);
      }
    },
  });

  const generateInvoiceMutation = useMutation({
    mutationFn: (paymentId) => feeAPI.generateInvoice(paymentId, true),
    onSuccess: (response) => {
      // Success is handled by the modal
      console.log('Invoice generated successfully');
    },
    onError: (error) => {
      console.error('Error generating invoice:', error);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      student_id: feeStructure.student_id || feeStructure.student?.id,
      batch_id: feeStructure.batch_id || feeStructure.batch?.id,
      fee_structure_id: feeStructureId,
      installment_id: installmentId, // Add installment ID if provided
      amount: parseFloat(formData.amount),
    };

    createPaymentMutation.mutate(submitData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateOutstanding = () => {
    if (!feeStructure) return 0;
    
    // Get enrollment fee payment for this student-batch combination
    const enrollment = enrollments?.find(enr => 
      (enr.student_id === feeStructure.student_id || enr.student?.id === feeStructure.student_id) &&
      (enr.batch_id === feeStructure.batch_id || enr.batch?.id === feeStructure.batch_id)
    );
    
    // Calculate enrollment fee paid
    const enrollmentFeePaid = enrollment?.enrollment_fee_paid ? (enrollment.enrollment_fee_amount || 0) : 0;
    
    // Calculate regular payments (excluding enrollment fee payments)
    const regularPayments = feeStructure.payments?.reduce((sum, payment) => {
      // Skip enrollment fee payments as they're handled separately
      if (payment.remarks && payment.remarks.toLowerCase().includes('enrollment fee')) {
        return sum;
      }
      return sum + payment.amount;
    }, 0) || 0;
    
    // Total paid includes both enrollment fee and regular payments
    const totalPaid = enrollmentFeePaid + regularPayments;
    

    
    return Math.max(0, feeStructure.final_amount - totalPaid);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
      </div>
    );
  }

  if (!feeStructure) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-300">Fee structure not found</p>
      </div>
    );
  }

  const outstanding = calculateOutstanding();

  return (
    <div className="min-h-screen bg-gray-900 py-4">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-4">
          <button
            onClick={() => navigate(`/fees/${feeStructureId}/payments`)}
            className="inline-flex items-center text-gray-400 hover:text-white transition-colors duration-200 mb-3 group"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
            <span className="text-sm font-medium">Back to Payments</span>
          </button>
          
          <div className="text-center sm:text-left">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Record Payment
            </h1>
            <p className="text-sm text-gray-300 max-w-2xl">
              Record a payment for this fee structure. Fill in the details below to complete the transaction.
            </p>
          </div>
        </div>

        {/* Fee Structure Summary Card */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-700 rounded-xl p-4 mb-4 border border-gray-600 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white flex items-center">
              <div className="w-1.5 h-6 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-full mr-3"></div>
              Fee Structure Summary
            </h2>
            <div className="hidden sm:block">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 rounded-full flex items-center justify-center">
                <div className="w-5 h-5 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full"></div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-600">
              <div className="text-xs text-gray-400 mb-1">Total Amount</div>
              <div className="text-lg font-bold text-white">{formatIndianCurrency(feeStructure.final_amount)}</div>
            </div>
            
            <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-600">
              <div className="text-xs text-gray-400 mb-1">Amount Paid</div>
              <div className="text-lg font-bold text-green-400">
                {formatIndianCurrency(feeStructure.final_amount - outstanding)}
              </div>
            </div>
            
            <div className="bg-red-900/20 border border-red-600/50 rounded-lg p-3">
              <div className="text-xs text-red-400 mb-1">Outstanding</div>
              <div className="text-lg font-bold text-red-400">{formatIndianCurrency(outstanding)}</div>
            </div>
          </div>
          
          {/* Installment Information */}
          {selectedInstallment && (
            <div className="mt-4 pt-4 border-t border-gray-600">
              <h3 className="text-sm font-semibold text-blue-400 mb-3 flex items-center">
                <div className="w-1.5 h-4 bg-blue-500 rounded-full mr-2"></div>
                Installment Payment Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-blue-900/20 border border-blue-600/50 rounded-lg p-2">
                  <div className="text-xs text-blue-400 mb-1">Installment</div>
                  <div className="text-xs font-medium text-white">{selectedInstallment.description || `Installment ${selectedInstallment.installment_number}`}</div>
                </div>
                <div className="bg-blue-900/20 border border-blue-600/50 rounded-lg p-2">
                  <div className="text-xs text-blue-400 mb-1">Due Date</div>
                  <div className="text-xs font-medium text-white">{formatDateToIST(selectedInstallment.due_date)}</div>
                </div>
                <div className="bg-blue-900/20 border border-blue-600/50 rounded-lg p-2">
                  <div className="text-xs text-blue-400 mb-1">Amount</div>
                  <div className="text-xs font-medium text-white">{formatIndianCurrency(selectedInstallment.amount)}</div>
                </div>
                <div className="bg-red-900/20 border border-red-600/50 rounded-lg p-2">
                  <div className="text-xs text-red-400 mb-1">Outstanding</div>
                  <div className="text-xs font-medium text-red-400">{formatIndianCurrency(selectedInstallment.outstanding_amount || selectedInstallment.amount)}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Payment Form Card */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-700 rounded-xl border border-gray-600 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-yellow-600/20 to-yellow-500/20 px-4 py-3 border-b border-yellow-600/30">
            <h3 className="text-lg font-bold text-white flex items-center">
              <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2 animate-pulse"></div>
              Payment Details
            </h3>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Payment Amount */}
            <div className="space-y-2">
              <label htmlFor="amount" className="block text-base font-semibold text-white">
                Payment Amount (₹) <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  min="0.01"
                  max={selectedInstallment ? (selectedInstallment.outstanding_amount || selectedInstallment.amount) : outstanding}
                  step="0.01"
                  required
                  className="block w-full px-3 py-2 text-base border-2 border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 bg-gray-700 text-white placeholder-gray-400 transition-all duration-200"
                  placeholder={selectedInstallment ? `Installment amount: ${formatIndianCurrency(selectedInstallment.outstanding_amount || selectedInstallment.amount)}` : `Maximum: ${formatIndianCurrency(outstanding)}`}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="w-5 h-5 bg-yellow-500/20 rounded-full flex items-center justify-center">
                    <span className="text-yellow-400 text-xs font-bold">₹</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-xs text-gray-400">
                <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                <span>
                  {selectedInstallment 
                    ? `Installment outstanding amount: ${formatIndianCurrency(selectedInstallment.outstanding_amount || selectedInstallment.amount)}`
                    : `Maximum payment amount: ${formatIndianCurrency(outstanding)}`
                  }
                </span>
              </div>
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <label htmlFor="payment_method" className="block text-base font-semibold text-white">
                Payment Method <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  id="payment_method"
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleChange}
                  required
                  className="block w-full px-3 py-2 text-base border-2 border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 bg-gray-700 text-white appearance-none cursor-pointer transition-all duration-200"
                >
                  <option value="cash">💵 Cash</option>
                  <option value="online">💳 Online Transfer</option>
                  <option value="cheque">🏦 Cheque</option>
                  <option value="card">💳 Card</option>
                </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Conditional Fields based on Payment Method */}
            {formData.payment_method === 'online' && (
              <div className="space-y-2">
                <label htmlFor="transaction_id" className="block text-base font-semibold text-white">
                  Transaction ID
                </label>
                <input
                  type="text"
                  id="transaction_id"
                  name="transaction_id"
                  value={formData.transaction_id}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 text-base border-2 border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 bg-gray-700 text-white placeholder-gray-400 transition-all duration-200"
                  placeholder="Enter transaction ID"
                />
              </div>
            )}

            {formData.payment_method === 'cheque' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="cheque_number" className="block text-base font-semibold text-white">
                    Cheque Number
                  </label>
                  <input
                    type="text"
                    id="cheque_number"
                    name="cheque_number"
                    value={formData.cheque_number}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 text-base border-2 border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 bg-gray-700 text-white placeholder-gray-400 transition-all duration-200"
                    placeholder="Enter cheque number"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="bank_name" className="block text-base font-semibold text-white">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    id="bank_name"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 text-base border-2 border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 bg-gray-700 text-white placeholder-gray-400 transition-all duration-200"
                    placeholder="Enter bank name"
                  />
                </div>
              </div>
            )}

            {/* Received By */}
            <div className="space-y-2">
              <label htmlFor="received_by" className="block text-base font-semibold text-white">
                Received By
              </label>
              <input
                type="text"
                id="received_by"
                name="received_by"
                value={formData.received_by}
                onChange={handleChange}
                className="block w-full px-3 py-2 text-base border-2 border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 bg-gray-700 text-white placeholder-gray-400 transition-all duration-200"
                placeholder="Enter name of person who received the payment"
              />
            </div>

            {/* Remarks */}
            <div className="space-y-2">
              <label htmlFor="remarks" className="block text-base font-semibold text-white">
                Remarks
              </label>
              <textarea
                id="remarks"
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                rows="3"
                className="block w-full px-3 py-2 text-base border-2 border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 bg-gray-700 text-white placeholder-gray-400 resize-none transition-all duration-200"
                placeholder="Any additional notes about this payment"
              />
            </div>

            {/* Error Display */}
            {createPaymentMutation.error && (
              <div className="bg-red-900/20 border-2 border-red-600/50 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <p className="text-red-300 font-medium">
                    {createPaymentMutation.error?.response?.data?.detail || 'An error occurred. Please try again.'}
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4 pt-4">
              <button
                type="button"
                onClick={() => navigate(`/fees/${feeStructureId}/payments`)}
                className="px-4 py-2 border-2 border-gray-600 rounded-lg shadow-sm text-base font-semibold text-gray-300 bg-gray-700 hover:bg-gray-600 hover:border-gray-500 transition-all duration-200 transform hover:scale-105"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createPaymentMutation.isPending || outstanding <= 0}
                className="px-6 py-2 border-2 border-transparent rounded-lg shadow-lg text-base font-bold text-white bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 disabled:transform-none"
              >
                {createPaymentMutation.isPending ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Recording Payment...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span>Record Payment</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <InvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        paymentData={paymentData}
        feeStructureId={feeStructureId}
        generateInvoiceMutation={generateInvoiceMutation}
      />
    </div>
  );
};

export default PaymentForm; 